import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Stack,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  RadioGroup,
  FormControlLabel,
  Radio,
  CircularProgress,
  Pagination,
  Switch,
  Select,
  MenuItem,
  Checkbox
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import InventoryIcon from '@mui/icons-material/Inventory';
import DescriptionIcon from '@mui/icons-material/Description';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BusinessIcon from '@mui/icons-material/Business';
import AddIcon from '@mui/icons-material/Add';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { StyledButton } from '../../../components';
import { useDatastore, useAlert, useCurrentUser } from '../../../hooks';
import { SSCCProps, ReceiptEventPackageProps, ReceiptEventPackageItemProps } from '../../../datastore/ssccs';
import { ReceiptPoStatus, SsccStatus } from '../../../common/status';
import useObserveSsccs from '../../../hooks/useSubscribeSsccs';
import dayjs from 'dayjs';
import { logAdminAction, AdminActionType } from '../../../services/auditLogger';
import { createSiteReceiptCoupa, updateManifestAsnsCoupa } from '../../../common/helperFunctions/coupa';
import { UpdateAsnProps } from '../../../api/postQM';

enum RemovalReason {
  PriorityShipping = 'priority_shipping',
  AppendToSSCC = 'append_to_sscc'
}

export default function SSCCManagement() {
  const store = useDatastore();
  const alert = useAlert();
  const user = useCurrentUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const ssccs = useObserveSsccs(null); // Load all SSCCs (no date limit)

  const [searchQuery, setSearchQuery] = useState(searchParams.get('sscc') || '');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedASN, setSelectedASN] = useState<{ sscc: SSCCProps; pkg: any; site: any } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [openRemoveDialog, setOpenRemoveDialog] = useState(false);
  const [removalReason, setRemovalReason] = useState<RemovalReason>(RemovalReason.PriorityShipping);
  const [actionReason, setActionReason] = useState('');
  const [targetSSCC, setTargetSSCC] = useState('');
  const [availableSSCCs, setAvailableSSCCs] = useState<SSCCProps[]>([]);
  const [expandedASN, setExpandedASN] = useState<string | null>(null);
  const [openAddASNDialog, setOpenAddASNDialog] = useState(false);
  const [selectedSSCCForASN, setSelectedSSCCForASN] = useState<SSCCProps | null>(null);
  const [asnNumber, setAsnNumber] = useState('');
  const [addASNReason, setAddASNReason] = useState('');
  const [availableASNs, setAvailableASNs] = useState<any[]>([]);
  const [hideEmptySSCCs, setHideEmptySSCCs] = useState(true);
  const [sortField, setSortField] = useState<'asn' | 'po' | 'status' | 'supplier' | 'dateReceived'>('asn');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [openCompleteDialog, setOpenCompleteDialog] = useState(false);
  const [selectedSSCCForComplete, setSelectedSSCCForComplete] = useState<SSCCProps | null>(null);
  const [completeReason, setCompleteReason] = useState('');

  // Multi-select state for bulk ASN removal
  const [selectionMode, setSelectionMode] = useState<string | null>(null); // SSCC ID when in selection mode
  const [selectedASNs, setSelectedASNs] = useState<Map<string, { sscc: SSCCProps; pkg: any; site: any }>>(new Map());
  const [openBulkRemoveDialog, setOpenBulkRemoveDialog] = useState(false);
  const [bulkRemoveReason, setBulkRemoveReason] = useState('');

  // Update URL when search query changes
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    if (searchQuery) {
      params.set('sscc', searchQuery);
    } else {
      params.delete('sscc');
    }
    setSearchParams(params, { replace: true });
  }, [searchQuery]);

  // Filter SSCCs based on search query
  let filteredSSCCs = ssccs;

  if (searchQuery.trim()) {
    filteredSSCCs = filteredSSCCs.filter(
      (sscc) =>
        sscc.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
        sscc.nickname?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        sscc.destinationLocation?.name?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }

  // Filter out SSCCs with no ASNs if toggle is active
  if (hideEmptySSCCs) {
    filteredSSCCs = filteredSSCCs.filter((sscc) => {
      const asnCount = sscc.receiptedEvents.reduce((acc: number, site: any) => acc + site.packages.length, 0);
      return asnCount > 0;
    });
  }

  // Pagination logic
  const pageSize = 10;
  const totalPages = Math.ceil(filteredSSCCs.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, filteredSSCCs.length);
  const paginatedSSCCs = filteredSSCCs.slice(startIndex, endIndex);

  // Reset to page 1 when search changes
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleOpenRemoveDialog = async (sscc: SSCCProps, pkg: any, site: any) => {
    setSelectedASN({ sscc, pkg, site });
    setOpenRemoveDialog(true);
    // Load available SSCCs for appending
    try {
      const allSSCCs = await store.ssccs.getList('', undefined, 'open', true);
      if (allSSCCs) {
        // Filter out the current SSCC and fetch locations
        const filteredSSCCs = allSSCCs.filter((s) => s.id !== sscc.id);

        // Fetch destination locations for each SSCC
        const ssccsWithLocations = await Promise.all(
          filteredSSCCs.map(async (s) => {
            const destinationLocation = s.destinationLocationId
              ? await store.locations.getById(s.destinationLocationId)
              : undefined;
            return {
              ...s,
              destinationLocation
            };
          })
        );

        setAvailableSSCCs(ssccsWithLocations);
      }
    } catch (error) {
      console.error('Error loading SSCCs:', error);
    }
  };

  const handleOpenAddASNDialog = async (sscc: SSCCProps) => {
    setSelectedSSCCForASN(sscc);
    setOpenAddASNDialog(true);
    // For now, we'll collect ASNs from all SSCCs with 'available' status
    // In a production environment, you would query a separate ASN/Receipt datastore
    try {
      const allSSCCs = await store.ssccs.getList('', undefined, undefined, true);
      if (allSSCCs) {
        // Collect all packages with 'available' status from all SSCCs
        const availablePackages: any[] = [];
        allSSCCs.forEach((s) => {
          s.receiptedEvents.forEach((event) => {
            event.packages.forEach((pkg) => {
              if (pkg.status === ReceiptPoStatus.available && pkg.asn) {
                // Check if this package is not already in the target SSCC
                const alreadyInTarget = sscc.receiptedEvents.some((e) =>
                  e.packages.some((p) => p.asn === pkg.asn && p.id === pkg.id)
                );
                if (!alreadyInTarget) {
                  availablePackages.push({
                    ...pkg,
                    sourceSSCC: s.id
                  });
                }
              }
            });
          });
        });

        setAvailableASNs(availablePackages);
      }
    } catch (error) {
      console.error('Error loading ASNs:', error);
    }
  };

  const handleAddASNToSSCC = async () => {
    if (!selectedSSCCForASN || !asnNumber || !addASNReason.trim() || isLoading) return;

    try {
      setIsLoading(true);

      // Get the ASN/package data
      const asnData = availableASNs.find((pkg) => pkg.asn === asnNumber);
      if (!asnData) {
        alert('ASN not found', 'error');
        return;
      }

      // Capture before state for audit
      const beforeState = {
        asn: asnNumber,
        status: asnData.status,
        sourceSSCC: asnData.sourceSSCC || 'Not in SSCC'
      };

      // Create a package object with all required fields from ReceiptEventPackageProps
      const newPackage: ReceiptEventPackageProps = {
        id: asnData.id || crypto.randomUUID(),
        asn: asnData.asn,
        po: asnData.po || null,
        status: selectedSSCCForASN.status || null, // Adopt SSCC status
        statusDate: dayjs(),
        supplierName: asnData.supplierName || null,
        supplierId: asnData.supplierId || null,
        companyName: asnData.companyName || '',
        quantity: asnData.quantity || null,
        packageType: asnData.packageType || null,
        priority: asnData.priority || null,
        jobId: asnData.jobId || null,
        assetNumber: asnData.assetNumber || null,
        height: asnData.height || null,
        width: asnData.width || null,
        depth: asnData.depth || null,
        weight: asnData.weight || null,
        hasDangerousGoods: asnData.hasDangerousGoods || false,
        dateReceived: asnData.dateReceived || dayjs().toISOString(),
        docketNumber: asnData.docketNumber || '',
        qrCodeId: asnData.qrCodeId || '',
        shipToAttention: asnData.shipToAttention || '',
        items: asnData.items || []
      };

      // Get the destination site name from SSCC
      const siteName = selectedSSCCForASN.destinationLocation?.name || 'Unknown';

      // Update SSCC with the new ASN
      const updatedSSCC = { ...selectedSSCCForASN };

      // Find or create the site in the SSCC
      let targetSite = updatedSSCC.receiptedEvents.find((e) => e.name === siteName);

      if (targetSite) {
        // Site exists, add package to it
        targetSite.packages.push(newPackage);
      } else {
        // Site doesn't exist, create it with the package
        updatedSSCC.receiptedEvents.push({
          name: siteName,
          site: selectedSSCCForASN.destinationLocationId || '',
          packages: [newPackage]
        });
      }

      await store.ssccs.update(updatedSSCC);

      // Remove the ASN from its source SSCC if it has one
      if (asnData.sourceSSCC) {
        const sourceSSCC = await store.ssccs.get(asnData.sourceSSCC);
        if (sourceSSCC) {
          const updatedSourceEvents = sourceSSCC.receiptedEvents
            .map((event) => ({
              ...event,
              packages: event.packages.filter((p) => p.id !== asnData.id)
            }))
            .filter((event) => event.packages.length > 0); // Remove empty sites

          const updatedSourceSSCC: SSCCProps = {
            ...sourceSSCC,
            receiptedEvents: updatedSourceEvents
          };

          await store.ssccs.update(updatedSourceSSCC);
        }
      }

      // Capture after state for audit
      const afterState = {
        asn: asnNumber,
        ssccId: selectedSSCCForASN.id,
        status: selectedSSCCForASN.status,
        site: siteName,
        removedFromSourceSSCC: asnData.sourceSSCC || null
      };

      // Log audit action
      await logAdminAction({
        actionType: AdminActionType.ASN_ASSIGN_TO_SSCC,
        beforeAction: beforeState,
        afterAction: afterState,
        actionBy: user.email,
        reason: addASNReason
      });

      // Publish ftl_site_receipt events for stock items to trigger inventory transfer
      const stockItems = (asnData.items || []).filter(
        (item: ReceiptEventPackageItemProps) =>
          item.purchaseType === 'Stock' && item.inTransitWarehouseId && item.inTransitWarehouseLocationId
      );

      if (stockItems.length > 0) {
        console.log(`Publishing ftl_site_receipt for ${stockItems.length} stock item(s) to transfer inventory`);

        const receiptedItem = {
          id: asnData.id,
          asn: asnData.asn,
          quantity: asnData.quantity ?? 0,
          items: stockItems.map((item: ReceiptEventPackageItemProps) => ({
            orderLineId: item.orderLineId,
            partId: item.partId,
            sourcePartId: item.sourcePartId ?? '',
            quantityReceived: item.quantityReceived ?? 0,
            purchaseType: item.purchaseType,
            receipt: dayjs(),
            receivingWarehouseId: item.receivingWarehouseId ?? null,
            receivingWarehouseDefaultLocationId: item.receivingWarehouseDefaultLocationId ?? null,
            inTransitWarehouseId: item.inTransitWarehouseId ?? null,
            inTransitWarehouseLocationId: item.inTransitWarehouseLocationId ?? null
          }))
        };

        try {
          const receiptResponse = await createSiteReceiptCoupa(receiptedItem);
          if (receiptResponse.error) {
            console.error('Error publishing ftl_site_receipt for ASN assignment:', receiptResponse.error);
            console.warn('Inventory may not be transferred. Manual reconciliation may be required.');
          } else {
            console.log(`Successfully published ftl_site_receipt for ASN ${asnData.asn}`);
          }
        } catch (receiptError) {
          console.error('Failed to publish ftl_site_receipt for ASN assignment:', receiptError);
          console.warn('Inventory may not be transferred. Manual reconciliation may be required.');
        }
      }

      alert('ASN successfully added to SSCC', 'success');
      setOpenAddASNDialog(false);
      setAsnNumber('');
      setAddASNReason('');
      setSelectedSSCCForASN(null);
    } catch (error) {
      console.error('Failed to add ASN to SSCC:', error);
      alert('An unexpected error occurred', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveASN = async () => {
    if (!selectedASN) return;

    if (!actionReason.trim()) {
      alert('Please provide a reason for this action', 'warning');
      return;
    }

    if (removalReason === RemovalReason.AppendToSSCC && !targetSSCC) {
      alert('Please select a target SSCC', 'warning');
      return;
    }

    try {
      setIsLoading(true);
      const { sscc, pkg, site } = selectedASN;

      if (removalReason === RemovalReason.PriorityShipping) {
        // Capture before state for audit
        const beforeState = {
          asn: pkg.asn,
          ssccId: sscc.id,
          status: pkg.status,
          site: site.name
        };

        // Set this specific ASN's status to 'available'
        const updatedEvents = sscc.receiptedEvents.map((event) => {
          if (event.name === site.name) {
            return {
              ...event,
              packages: event.packages.map((p) => (p.id === pkg.id ? { ...p, status: ReceiptPoStatus.available } : p))
            };
          }
          return event;
        });

        const updatedSSCC: SSCCProps = {
          ...sscc,
          receiptedEvents: updatedEvents
        };

        await store.ssccs.update(updatedSSCC);

        // Publish ftl_site_receipt events for stock items to trigger GIW inventory transfer
        const stockItems = (pkg.items || []).filter(
          (item: ReceiptEventPackageItemProps) =>
            item.purchaseType === 'Stock' && item.inTransitWarehouseId && item.inTransitWarehouseLocationId
        );

        if (stockItems.length > 0) {
          console.log(`Publishing ftl_site_receipt for ${stockItems.length} stock item(s) to transfer GIW inventory`);

          const receiptedItem = {
            id: pkg.id,
            asn: pkg.asn,
            quantity: pkg.quantity ?? 0,
            items: stockItems.map((item: ReceiptEventPackageItemProps) => ({
              orderLineId: item.orderLineId,
              partId: item.partId,
              sourcePartId: item.sourcePartId ?? '',
              quantityReceived: item.quantityReceived ?? 0,
              purchaseType: item.purchaseType,
              receipt: dayjs(),
              receivingWarehouseId: item.receivingWarehouseId ?? null,
              receivingWarehouseDefaultLocationId: item.receivingWarehouseDefaultLocationId ?? null,
              inTransitWarehouseId: item.inTransitWarehouseId ?? null,
              inTransitWarehouseLocationId: item.inTransitWarehouseLocationId ?? null
            }))
          };

          try {
            const receiptResponse = await createSiteReceiptCoupa(receiptedItem);
            if (receiptResponse.error) {
              console.error('Error publishing ftl_site_receipt for priority shipping:', receiptResponse.error);
              console.warn('GIW inventory may not be transferred. Manual reconciliation may be required.');
            } else {
              console.log(`Successfully published ftl_site_receipt for ASN ${pkg.asn}`);
            }
          } catch (receiptError) {
            console.error('Failed to publish ftl_site_receipt for priority shipping:', receiptError);
            console.warn('GIW inventory may not be transferred. Manual reconciliation may be required.');
          }
        }

        // Log audit action for priority shipping
        await logAdminAction({
          actionType: AdminActionType.ASN_UNSTAGE,
          beforeAction: beforeState,
          afterAction: {
            asn: pkg.asn,
            ssccId: sscc.id,
            status: ReceiptPoStatus.available,
            site: site.name
          },
          actionBy: user.email,
          reason: actionReason
        });

        alert('ASN marked as available for priority shipping', 'success');
      } else {
        // Move this specific ASN to another SSCC
        const targetSSCCData = await store.ssccs.get(targetSSCC);
        if (!targetSSCCData) {
          alert('Target SSCC not found', 'error');
          return;
        }

        // Capture before state for audit
        const beforeState = {
          asn: pkg.asn,
          sourceSSCC: sscc.id,
          status: pkg.status,
          site: site.name
        };

        // Update the package status to match target SSCC
        const updatedPkg = { ...pkg, status: targetSSCCData.status, statusDate: dayjs() };

        // Find or create the site in target SSCC
        let targetSite = targetSSCCData.receiptedEvents.find((e) => e.name === site.name);

        if (targetSite) {
          // Site exists, add package to it
          targetSite.packages.push(updatedPkg);
        } else {
          // Site doesn't exist, create it with the package
          targetSSCCData.receiptedEvents.push({
            name: site.name,
            site: site.site,
            packages: [updatedPkg]
          });
        }

        await store.ssccs.update(targetSSCCData);

        // Remove the package from source SSCC
        const updatedSourceEvents = sscc.receiptedEvents
          .map((event) => {
            if (event.name === site.name) {
              return {
                ...event,
                packages: event.packages.filter((p) => p.id !== pkg.id)
              };
            }
            return event;
          })
          .filter((event) => event.packages.length > 0); // Remove empty sites

        const updatedSourceSSCC: SSCCProps = {
          ...sscc,
          receiptedEvents: updatedSourceEvents
        };

        await store.ssccs.update(updatedSourceSSCC);

        // Log audit action for ASN reassignment
        await logAdminAction({
          actionType: AdminActionType.ASN_ASSIGN_TO_SSCC,
          beforeAction: beforeState,
          afterAction: {
            asn: pkg.asn,
            sourceSSCC: sscc.id,
            targetSSCC: targetSSCC,
            status: targetSSCCData.status,
            site: site.name
          },
          actionBy: user.email,
          reason: actionReason
        });

        alert(`ASN successfully moved to SSCC ${targetSSCC}`, 'success');
      }

      setOpenRemoveDialog(false);
      setSelectedASN(null);
      setRemovalReason(RemovalReason.PriorityShipping);
      setActionReason('');
      setTargetSSCC('');
    } catch (error) {
      console.error('Error removing ASN:', error);
      alert('Error removing ASN', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const sortPackages = (packages: any[]) => {
    return [...packages].sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case 'asn':
          comparison = (a.asn || '').localeCompare(b.asn || '');
          break;
        case 'po':
          comparison = (a.po || '').localeCompare(b.po || '');
          break;
        case 'status':
          comparison = (a.status || '').localeCompare(b.status || '');
          break;
        case 'supplier':
          comparison = (a.supplierName || '').localeCompare(b.supplierName || '');
          break;
        case 'dateReceived':
          comparison = new Date(a.dateReceived || 0).getTime() - new Date(b.dateReceived || 0).getTime();
          break;
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  };

  const handleCompleteSSCC = async () => {
    if (!selectedSSCCForComplete || !completeReason.trim() || isLoading) return;

    try {
      setIsLoading(true);

      const beforeState = {
        ssccId: selectedSSCCForComplete.id,
        status: selectedSSCCForComplete.status,
        asnCount: selectedSSCCForComplete.receiptedEvents.reduce((acc, site) => acc + site.packages.length, 0)
      };

      // Extract all ASNs from the SSCC
      const allAsns: string[] = selectedSSCCForComplete.receiptedEvents
        .flatMap((site) => site.packages)
        .map((pkg) => pkg.asn)
        .filter((asn): asn is string => asn !== null && asn !== undefined && asn !== '');

      // Update SSCC status to Delivered (completed state)
      const updatedSSCC: SSCCProps = {
        ...selectedSSCCForComplete,
        status: SsccStatus.delivered,
        delivered: dayjs()
      };

      await store.ssccs.update(updatedSSCC);

      // Send ASNs to Coupa to mark as Available
      if (allAsns.length > 0) {
        console.log(`Updating ${allAsns.length} ASN(s) to Available status in Coupa`);
        const asnUpdateEvent: UpdateAsnProps = {
          asns: allAsns
        };

        try {
          const coupaResponse = await updateManifestAsnsCoupa(asnUpdateEvent);
          if (coupaResponse.error) {
            console.error('Error updating ASNs in Coupa:', coupaResponse.error);
            // Note: We don't fail the operation - SSCC is already completed in FTL
            // The Coupa update failure is logged but operation continues
          } else {
            console.log(`Successfully updated ${allAsns.length} ASN(s) to Available status in Coupa`);
          }
        } catch (coupaError) {
          console.error('Failed to update Coupa:', coupaError);
          // Continue - SSCC completion in FTL is successful
        }
      }

      // Log audit action
      await logAdminAction({
        actionType: AdminActionType.SSCC_COMPLETE,
        beforeAction: beforeState,
        afterAction: {
          ssccId: selectedSSCCForComplete.id,
          status: SsccStatus.delivered,
          completedAt: dayjs().toISOString(),
          asnsUpdatedInCoupa: allAsns.length
        },
        actionBy: user.email,
        reason: completeReason
      });

      alert('SSCC successfully completed', 'success');
      setOpenCompleteDialog(false);
      setCompleteReason('');
      setSelectedSSCCForComplete(null);
    } catch (error) {
      console.error('Error completing SSCC:', error);
      alert('Error completing SSCC', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBulkRemoveASNs = async () => {
    if (selectedASNs.size === 0 || !bulkRemoveReason.trim() || isLoading) return;

    try {
      setIsLoading(true);

      // Group ASNs by source SSCC for efficient updates
      const asnsBySSCC = new Map<string, Array<{ pkg: any; site: any }>>();
      selectedASNs.forEach(({ sscc, pkg, site }) => {
        const existing = asnsBySSCC.get(sscc.id) || [];
        existing.push({ pkg, site });
        asnsBySSCC.set(sscc.id, existing);
      });

      // Process each SSCC
      for (const [ssccId, asns] of asnsBySSCC) {
        const sscc = await store.ssccs.get(ssccId);
        if (!sscc) continue;

        // Capture before state for audit with detailed ASN information
        const beforeState = {
          ssccId: sscc.id,
          ssccStatus: sscc.status,
          ssccDestination: sscc.destinationLocation?.name || 'Unknown',
          totalAsnsBeforeRemoval: sscc.receiptedEvents.reduce((acc, e) => acc + e.packages.length, 0),
          asnsToBeRemoved: asns.map((a) => ({
            asn: a.pkg.asn,
            po: a.pkg.po,
            site: a.site?.name || 'Unknown',
            status: a.pkg.status,
            supplierName: a.pkg.supplierName || 'Unknown',
            quantity: a.pkg.quantity || 0,
            dateReceived: a.pkg.dateReceived || 'Unknown'
          }))
        };

        // Get package IDs to remove
        const pkgIdsToRemove = new Set(asns.map((a) => a.pkg.id));

        // Remove the packages from SSCC
        const updatedEvents = sscc.receiptedEvents
          .map((event) => ({
            ...event,
            packages: event.packages.filter((p) => !pkgIdsToRemove.has(p.id))
          }))
          .filter((event) => event.packages.length > 0); // Remove empty sites

        const updatedSSCC: SSCCProps = {
          ...sscc,
          receiptedEvents: updatedEvents
        };

        await store.ssccs.update(updatedSSCC);

        // Log audit action with detailed after state
        await logAdminAction({
          actionType: AdminActionType.ASN_DELETE,
          beforeAction: beforeState,
          afterAction: {
            ssccId: sscc.id,
            ssccStatus: sscc.status,
            ssccDestination: sscc.destinationLocation?.name || 'Unknown',
            asnsRemovedCount: asns.length,
            asnsRemovedList: asns.map((a) => a.pkg.asn).filter(Boolean),
            remainingAsnsCount: updatedEvents.reduce((acc, e) => acc + e.packages.length, 0),
            remainingAsns: updatedEvents.flatMap((e) => e.packages.map((p) => p.asn)).filter(Boolean),
            deletedAt: dayjs().toISOString()
          },
          actionBy: user.email,
          reason: bulkRemoveReason
        });
      }

      alert(`Successfully removed ${selectedASNs.size} ASN(s)`, 'success');
      setOpenBulkRemoveDialog(false);
      setBulkRemoveReason('');
      setSelectionMode(null);
      setSelectedASNs(new Map());
    } catch (error) {
      console.error('Error removing ASNs:', error);
      alert('Error removing ASNs', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Stack spacing={3} sx={{ px: 3 }}>
      {/* Search Section */}
      <Card
        sx={{
          boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
          border: '1px solid rgba(0,0,0,0.06)',
          borderRadius: 2
        }}>
        <CardContent sx={{ p: 3 }}>
          <Stack spacing={2.5}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  p: 1,
                  borderRadius: 1.5,
                  backgroundColor: 'rgba(25, 118, 210, 0.08)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                <SearchIcon sx={{ color: '#1976d2', fontSize: 20 }} />
              </Box>
              <Typography variant="h6" fontWeight={600} sx={{ color: 'text.primary' }}>
                Search SSCC
              </Typography>
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center">
              <TextField
                fullWidth
                placeholder="Search by SSCC ID, nickname, or destination..."
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                size="small"
                InputProps={{
                  startAdornment: <InventoryIcon sx={{ color: 'text.secondary', mr: 1, fontSize: 20 }} />
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(0,0,0,0.02)',
                    '&:hover': {
                      backgroundColor: 'rgba(0,0,0,0.03)'
                    },
                    '&.Mui-focused': {
                      backgroundColor: 'background.default'
                    }
                  }
                }}
              />
              {searchQuery && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setSearchQuery('');
                    setCurrentPage(1);
                  }}
                  sx={{ whiteSpace: 'nowrap' }}>
                  Clear Filter
                </Button>
              )}
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* SSCCs List Section */}
      {filteredSSCCs.length > 0 && (
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight={600}>
              SSCCs ({filteredSSCCs.length})
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <FormControlLabel
                control={
                  <Switch checked={hideEmptySSCCs} onChange={(e) => setHideEmptySSCCs(e.target.checked)} size="small" />
                }
                label={
                  <Typography variant="body2" fontWeight={500} sx={{ color: 'text.secondary' }}>
                    Hide Empty SSCCs
                  </Typography>
                }
              />
              <Typography variant="body2" color="text.secondary">
                Page {currentPage} of {totalPages}
              </Typography>
            </Stack>
          </Stack>

          {paginatedSSCCs.map((sscc) => {
            const asnCount = sscc.receiptedEvents.reduce((acc: number, site: any) => acc + site.packages.length, 0);
            const statusLower = sscc.status?.toLowerCase() || '';

            // Color mapping based on status
            const getStatusColor = () => {
              if (statusLower === 'delivered') return '#2e7d32';
              if (statusLower === 'in transit' || statusLower === 'intransit') return '#1565c0';
              if (statusLower === 'approaching') return '#e65100';
              if (statusLower === 'manifested') return '#5e35b1';
              if (statusLower === 'staged') return '#00838f';
              if (statusLower === 'open') return '#546e7a';
              if (statusLower === 'cancelled') return '#c62828';
              return '#757575';
            };

            const getStatusGlow = () => {
              if (statusLower === 'delivered') return '0 0 12px rgba(46, 125, 50, 0.25)';
              if (statusLower === 'in transit' || statusLower === 'intransit')
                return '0 0 12px rgba(21, 101, 192, 0.25)';
              if (statusLower === 'approaching') return '0 0 12px rgba(230, 81, 0, 0.25)';
              if (statusLower === 'manifested') return '0 0 12px rgba(94, 53, 177, 0.25)';
              if (statusLower === 'staged') return '0 0 12px rgba(0, 131, 143, 0.25)';
              if (statusLower === 'open') return '0 0 12px rgba(84, 110, 122, 0.25)';
              if (statusLower === 'cancelled') return '0 0 12px rgba(198, 40, 40, 0.25)';
              return '0 0 12px rgba(0, 0, 0, 0.08)';
            };

            const getStatusBgColor = () => {
              if (statusLower === 'delivered') return 'rgba(46, 125, 50, 0.1)';
              if (statusLower === 'in transit' || statusLower === 'intransit') return 'rgba(21, 101, 192, 0.1)';
              if (statusLower === 'approaching') return 'rgba(230, 81, 0, 0.1)';
              if (statusLower === 'manifested') return 'rgba(94, 53, 177, 0.1)';
              if (statusLower === 'staged') return 'rgba(0, 131, 143, 0.1)';
              if (statusLower === 'open') return 'rgba(84, 110, 122, 0.1)';
              if (statusLower === 'cancelled') return 'rgba(198, 40, 40, 0.1)';
              return 'rgba(0, 0, 0, 0.06)';
            };

            const statusColor = getStatusColor();
            const statusGlow = getStatusGlow();
            const statusBgColor = getStatusBgColor();

            return (
              <Accordion
                key={sscc.id}
                sx={{
                  boxShadow: 'none',
                  border: '1px solid',
                  borderColor: 'rgba(0,0,0,0.08)',
                  borderRadius: '12px !important',
                  '&:before': { display: 'none' },
                  marginBottom: '16px !important',
                  overflow: 'hidden',
                  borderLeft: '3px solid',
                  borderLeftColor: statusColor,
                  transition: 'all 0.2s ease',
                  '&.Mui-expanded': {
                    margin: '0 0 16px 0 !important',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                  },
                  '& + .MuiAccordion-root': {
                    marginTop: 0
                  },
                  '&:hover': {
                    borderTopColor: 'rgba(0,0,0,0.15)',
                    borderRightColor: 'rgba(0,0,0,0.15)',
                    borderBottomColor: 'rgba(0,0,0,0.15)',
                    boxShadow: statusGlow
                  }
                }}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: 'text.secondary' }} />}
                  sx={{
                    px: 3,
                    py: 1,
                    '&:hover': { backgroundColor: 'transparent' },
                    '& .MuiAccordionSummary-content': { my: 1.5 }
                  }}>
                  <Stack direction="row" spacing={3} alignItems="center" sx={{ width: '100%' }}>
                    <Box sx={{ flex: 1 }}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Typography variant="body1" sx={{ fontWeight: 600, letterSpacing: '-0.5px', color: '#1565c0' }}>
                          {sscc.id}
                        </Typography>
                        <Box
                          sx={{
                            px: 1.5,
                            py: 0.25,
                            borderRadius: '100px',
                            backgroundColor: statusBgColor,
                            display: 'inline-flex'
                          }}>
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 600,
                              fontSize: '0.7rem',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                              color: statusColor
                            }}>
                            {sscc.status || 'Unknown'}
                          </Typography>
                        </Box>
                      </Stack>
                      <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                        {sscc.destinationLocation?.name || 'Unknown destination'}
                        {sscc.nickname && (
                          <Box component="span" sx={{ color: 'text.disabled' }}>
                            {' '}
                            · {sscc.nickname}
                          </Box>
                        )}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, whiteSpace: 'nowrap' }}>
                      {asnCount} {asnCount === 1 ? 'ASN' : 'ASNs'}
                    </Typography>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 3, pb: 3, pt: 0 }}>
                  <Stack spacing={3}>
                    {/* SSCC Details - Inline chips */}
                    <Stack direction="row" spacing={3} sx={{ pt: 1 }}>
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.disabled',
                            fontSize: '0.65rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}>
                          Origin
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.25 }}>
                          {sscc.originLocation?.name || 'Unknown'}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.disabled',
                            fontSize: '0.65rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}>
                          Type
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.25 }}>
                          {sscc.ssccType || 'Standard'}
                        </Typography>
                      </Box>
                      {sscc.nickname && (
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{
                              color: 'text.disabled',
                              fontSize: '0.65rem',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}>
                            Nickname
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.25 }}>
                            {sscc.nickname}
                          </Typography>
                        </Box>
                      )}
                    </Stack>

                    {/* ASNs Section */}
                    <Box>
                      {/* Header */}
                      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                          {asnCount} {asnCount === 1 ? 'ASN' : 'ASNs'}
                        </Typography>

                        <Stack direction="row" spacing={0.5} alignItems="center">
                          {/* Action Buttons */}
                          {selectionMode === sscc.id ? (
                            <>
                              <Button
                                size="small"
                                onClick={() => {
                                  setSelectionMode(null);
                                  setSelectedASNs(new Map());
                                }}
                                sx={{
                                  fontSize: '0.75rem',
                                  textTransform: 'none',
                                  color: 'text.secondary',
                                  minWidth: 'auto',
                                  px: 1.5,
                                  py: 0.5,
                                  borderRadius: '6px'
                                }}>
                                Cancel
                              </Button>
                              <Button
                                size="small"
                                disabled={selectedASNs.size === 0}
                                onClick={() => setOpenBulkRemoveDialog(true)}
                                sx={{
                                  fontSize: '0.75rem',
                                  textTransform: 'none',
                                  minWidth: 'auto',
                                  px: 1.5,
                                  py: 0.5,
                                  borderRadius: '6px',
                                  backgroundColor: selectedASNs.size > 0 ? '#ef5350' : 'rgba(0,0,0,0.08)',
                                  color: selectedASNs.size > 0 ? 'background.default' : 'text.disabled',
                                  '&:hover': {
                                    backgroundColor: selectedASNs.size > 0 ? '#e53935' : 'rgba(0,0,0,0.08)'
                                  }
                                }}>
                                Remove {selectedASNs.size}
                              </Button>
                            </>
                          ) : (
                            <>
                              {asnCount > 1 && (
                                <Button
                                  size="small"
                                  onClick={() => {
                                    setSelectionMode(sscc.id);
                                    setSelectedASNs(new Map());
                                  }}
                                  sx={{
                                    fontSize: '0.75rem',
                                    textTransform: 'none',
                                    color: 'text.secondary',
                                    minWidth: 'auto',
                                    px: 1.5,
                                    py: 0.5,
                                    borderRadius: '6px',
                                    '&:hover': {
                                      backgroundColor: 'rgba(0,0,0,0.04)'
                                    }
                                  }}>
                                  Select
                                </Button>
                              )}
                              <Button
                                size="small"
                                onClick={() => handleOpenAddASNDialog(sscc)}
                                sx={{
                                  fontSize: '0.75rem',
                                  textTransform: 'none',
                                  color: 'text.secondary',
                                  minWidth: 'auto',
                                  px: 1.5,
                                  py: 0.5,
                                  borderRadius: '6px',
                                  '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' }
                                }}>
                                Add ASN
                              </Button>
                              {sscc.status !== SsccStatus.delivered && (
                                <Button
                                  size="small"
                                  onClick={() => {
                                    setSelectedSSCCForComplete(sscc);
                                    setOpenCompleteDialog(true);
                                  }}
                                  sx={{
                                    fontSize: '0.75rem',
                                    textTransform: 'none',
                                    color: '#2e7d32',
                                    minWidth: 'auto',
                                    px: 1.5,
                                    py: 0.5,
                                    borderRadius: '6px',
                                    '&:hover': {
                                      backgroundColor: 'rgba(46, 125, 50, 0.08)'
                                    }
                                  }}>
                                  Complete
                                </Button>
                              )}
                              {asnCount > 0 && (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    ml: 1,
                                    pl: 1,
                                    borderLeft: '1px solid rgba(0,0,0,0.08)'
                                  }}>
                                  <Select
                                    value={sortField}
                                    size="small"
                                    variant="standard"
                                    disableUnderline
                                    sx={{
                                      fontSize: '0.75rem',
                                      color: 'text.secondary',
                                      '& .MuiSelect-select': { py: 0, pr: 2 }
                                    }}
                                    onChange={(e) => setSortField(e.target.value as typeof sortField)}>
                                    <MenuItem value="asn" sx={{ fontSize: '0.8rem' }}>
                                      ASN
                                    </MenuItem>
                                    <MenuItem value="po" sx={{ fontSize: '0.8rem' }}>
                                      PO
                                    </MenuItem>
                                    <MenuItem value="status" sx={{ fontSize: '0.8rem' }}>
                                      Status
                                    </MenuItem>
                                    <MenuItem value="supplier" sx={{ fontSize: '0.8rem' }}>
                                      Supplier
                                    </MenuItem>
                                    <MenuItem value="dateReceived" sx={{ fontSize: '0.8rem' }}>
                                      Date
                                    </MenuItem>
                                  </Select>
                                  <IconButton
                                    size="small"
                                    onClick={() => setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'))}
                                    sx={{ ml: -0.5, p: 0.5 }}>
                                    {sortDirection === 'asc' ? (
                                      <ArrowUpwardIcon
                                        sx={{
                                          fontSize: 14,
                                          color: 'text.secondary'
                                        }}
                                      />
                                    ) : (
                                      <ArrowDownwardIcon
                                        sx={{
                                          fontSize: 16,
                                          color: 'text.secondary'
                                        }}
                                      />
                                    )}
                                  </IconButton>
                                </Box>
                              )}
                            </>
                          )}
                        </Stack>
                      </Stack>

                      {/* Selection mode alert */}
                      {selectionMode === sscc.id && (
                        <Alert
                          severity="warning"
                          sx={{
                            mb: 1.5,
                            py: 0.5,
                            borderRadius: 1,
                            '& .MuiAlert-message': { py: 0.5 }
                          }}>
                          <Typography variant="body2" fontSize="0.8rem">
                            Select ASNs to remove ({selectedASNs.size} selected)
                          </Typography>
                        </Alert>
                      )}

                      {asnCount === 0 ? (
                        <Alert severity="info">No ASNs linked to this SSCC</Alert>
                      ) : (
                        <Stack spacing={1}>
                          {sortPackages(
                            sscc.receiptedEvents.flatMap((site: any) =>
                              site.packages.map((pkg: any) => ({
                                ...pkg,
                                siteName: site.name,
                                siteObj: site
                              }))
                            )
                          ).map((pkg: any) => {
                            const asnKey = `${sscc.id}-${pkg.siteName}-${pkg.id}`;
                            const isExpanded = expandedASN === asnKey;
                            const isInSelectionMode = selectionMode === sscc.id;
                            const isSelected = selectedASNs.has(asnKey);

                            return (
                              <Accordion
                                key={asnKey}
                                expanded={isExpanded}
                                onChange={() => {
                                  if (!isInSelectionMode) {
                                    setExpandedASN(isExpanded ? null : asnKey);
                                  }
                                }}
                                sx={{
                                  boxShadow: 'none',
                                  border: '1px solid',
                                  borderColor: isSelected ? 'error.main' : 'rgba(0,0,0,0.06)',
                                  borderRadius: '8px !important',
                                  backgroundColor: isSelected ? 'rgba(211, 47, 47, 0.03)' : 'background.paper',
                                  transition: 'all 0.15s ease',
                                  '&:before': { display: 'none' },
                                  borderLeft: '2px solid',
                                  borderLeftColor: isSelected
                                    ? 'error.main'
                                    : pkg.status === ReceiptPoStatus.available
                                      ? '#4caf50'
                                      : '#64b5f6',
                                  '&:hover': {
                                    borderColor: isSelected ? 'error.main' : 'rgba(0,0,0,0.12)',
                                    backgroundColor: isSelected ? 'rgba(211, 47, 47, 0.05)' : '#f5f5f5'
                                  },
                                  overflow: 'hidden'
                                }}>
                                <AccordionSummary
                                  expandIcon={
                                    !isInSelectionMode ? (
                                      <ExpandMoreIcon
                                        sx={{
                                          fontSize: 18,
                                          color: 'text.disabled'
                                        }}
                                      />
                                    ) : null
                                  }
                                  sx={{
                                    minHeight: 48,
                                    px: 2,
                                    '& .MuiAccordionSummary-content': { my: 1 },
                                    cursor: isInSelectionMode ? 'pointer' : 'default',
                                    '&:hover': { backgroundColor: 'transparent' }
                                  }}
                                  onClick={(e) => {
                                    if (isInSelectionMode) {
                                      e.stopPropagation();
                                      const newSelected = new Map(selectedASNs);
                                      if (isSelected) {
                                        newSelected.delete(asnKey);
                                      } else {
                                        newSelected.set(asnKey, {
                                          sscc,
                                          pkg,
                                          site: pkg.siteObj
                                        });
                                      }
                                      setSelectedASNs(newSelected);
                                    }
                                  }}>
                                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: '100%' }}>
                                    {isInSelectionMode ? (
                                      <Checkbox
                                        checked={isSelected}
                                        size="small"
                                        sx={{
                                          p: 0,
                                          color: 'error.main',
                                          '&.Mui-checked': {
                                            color: 'error.main'
                                          }
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={() => {
                                          const newSelected = new Map(selectedASNs);
                                          if (isSelected) {
                                            newSelected.delete(asnKey);
                                          } else {
                                            newSelected.set(asnKey, {
                                              sscc,
                                              pkg,
                                              site: pkg.siteObj
                                            });
                                          }
                                          setSelectedASNs(newSelected);
                                        }}
                                      />
                                    ) : (
                                      <DescriptionIcon
                                        sx={{
                                          fontSize: 18,
                                          color: pkg.status === ReceiptPoStatus.available ? '#4caf50' : '#64b5f6'
                                        }}
                                      />
                                    )}
                                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                      <Stack direction="row" spacing={1} alignItems="center">
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            fontWeight: 600,

                                            fontSize: '0.85rem',
                                            color: '#37474f'
                                          }}>
                                          {pkg.asn || 'N/A'}
                                        </Typography>
                                        {pkg.status && (
                                          <Box
                                            sx={{
                                              px: 1,
                                              py: 0.125,
                                              borderRadius: '100px',
                                              backgroundColor:
                                                pkg.status === ReceiptPoStatus.available
                                                  ? 'rgba(46, 125, 50, 0.1)'
                                                  : 'rgba(0, 0, 0, 0.06)'
                                            }}>
                                            <Typography
                                              variant="caption"
                                              sx={{
                                                fontWeight: 600,
                                                fontSize: '0.65rem',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.3px',
                                                color:
                                                  pkg.status === ReceiptPoStatus.available
                                                    ? '#2e7d32'
                                                    : 'text.secondary'
                                              }}>
                                              {pkg.status}
                                            </Typography>
                                          </Box>
                                        )}
                                      </Stack>
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          color: 'text.disabled',
                                          fontSize: '0.75rem'
                                        }}>
                                        PO: {pkg.po || 'N/A'} · {pkg.siteName} ·{' '}
                                        {pkg.supplierName || 'Unknown supplier'}
                                      </Typography>
                                    </Box>
                                  </Stack>
                                </AccordionSummary>
                                <AccordionDetails sx={{ px: 2, pt: 0, pb: 2 }}>
                                  <Stack spacing={2}>
                                    {/* Details Grid */}
                                    <Stack direction="row" spacing={4} sx={{ pt: 1 }}>
                                      <Box>
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            color: 'text.disabled',
                                            fontSize: '0.65rem',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px'
                                          }}>
                                          Supplier
                                        </Typography>
                                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.25 }}>
                                          <BusinessIcon
                                            sx={{
                                              fontSize: 14,
                                              color: 'text.secondary'
                                            }}
                                          />
                                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {pkg.supplierName || 'Unknown'}
                                          </Typography>
                                        </Stack>
                                      </Box>
                                      <Box>
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            color: 'text.disabled',
                                            fontSize: '0.65rem',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px'
                                          }}>
                                          Company
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.25 }}>
                                          {pkg.companyName || 'N/A'}
                                        </Typography>
                                      </Box>
                                      <Box>
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            color: 'text.disabled',
                                            fontSize: '0.65rem',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px'
                                          }}>
                                          Quantity
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.25 }}>
                                          {pkg.quantity || 'N/A'}
                                        </Typography>
                                      </Box>
                                      <Box>
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            color: 'text.disabled',
                                            fontSize: '0.65rem',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px'
                                          }}>
                                          Package Type
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.25 }}>
                                          {pkg.packageType || 'Standard'}
                                        </Typography>
                                      </Box>
                                    </Stack>

                                    {/* Additional Info Row */}
                                    <Stack direction="row" spacing={4}>
                                      <Box>
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            color: 'text.disabled',
                                            fontSize: '0.65rem',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px'
                                          }}>
                                          Line Items
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.25 }}>
                                          {pkg.items?.length || 0} items
                                        </Typography>
                                      </Box>
                                    </Stack>

                                    {/* Dangerous Goods Warning */}
                                    {pkg.hasDangerousGoods && (
                                      <Alert
                                        severity="warning"
                                        sx={{
                                          borderRadius: 1,
                                          py: 0.5,
                                          '& .MuiAlert-message': { py: 0 }
                                        }}>
                                        Contains Dangerous Goods
                                      </Alert>
                                    )}

                                    {/* Action Button */}
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        justifyContent: 'flex-end',
                                        pt: 1
                                      }}>
                                      <Button
                                        variant="text"
                                        size="small"
                                        color="error"
                                        startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />}
                                        onClick={() => handleOpenRemoveDialog(sscc, pkg, pkg.siteObj)}
                                        sx={{
                                          fontSize: '0.75rem',
                                          textTransform: 'none',
                                          fontWeight: 500,
                                          px: 1.5,
                                          py: 0.5,
                                          borderRadius: '6px',
                                          '&:hover': {
                                            backgroundColor: 'rgba(211, 47, 47, 0.08)'
                                          }
                                        }}>
                                        Remove ASN
                                      </Button>
                                    </Box>
                                  </Stack>
                                </AccordionDetails>
                              </Accordion>
                            );
                          })}
                        </Stack>
                      )}
                    </Box>
                  </Stack>
                </AccordionDetails>
              </Accordion>
            );
          })}

          {/* Pagination */}
          {totalPages > 1 && (
            <Stack direction="row" justifyContent="center" sx={{ pt: 2 }}>
              <Pagination count={totalPages} page={currentPage} onChange={handlePageChange} color="primary" />
            </Stack>
          )}
        </Stack>
      )}

      {/* No Results */}
      {filteredSSCCs.length === 0 && searchQuery && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <InventoryIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No SSCCs found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try searching with a different SSCC ID, nickname, or destination
          </Typography>
        </Box>
      )}

      {/* Loading State */}
      {ssccs.length === 0 && !searchQuery && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Loading SSCCs...
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Syncing data from DataStore
          </Typography>
        </Box>
      )}

      {/* Empty State */}
      {ssccs.length > 0 && filteredSSCCs.length === 0 && !searchQuery && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <InventoryIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No SSCCs available
          </Typography>
          <Typography variant="body2" color="text.secondary">
            SSCCs will appear here once they are created
          </Typography>
        </Box>
      )}

      {/* Remove ASN Dialog */}
      <Dialog
        open={openRemoveDialog}
        onClose={() => setOpenRemoveDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}>
        <DialogTitle
          sx={{
            borderBottom: '1px solid rgba(0,0,0,0.08)',
            px: 3,
            py: 2.5,
            backgroundColor: '#fef6f6'
          }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                p: 1,
                borderRadius: 1.5,
                backgroundColor: 'background.default',
                display: 'flex',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
              }}>
              <DeleteOutlineIcon sx={{ fontSize: 22, color: '#d32f2f' }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={600}>
                Remove/Reassign ASN
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Choose how to handle this ASN
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 3 }}>
          <Stack spacing={3}>
            <Alert
              severity="warning"
              icon={<DeleteOutlineIcon />}
              sx={{
                borderRadius: 1.5,
                border: '1px solid rgba(237, 108, 2, 0.2)'
              }}>
              This action will remove ASN <strong>{selectedASN?.pkg.asn || 'N/A'}</strong> from SSCC{' '}
              <strong>{selectedASN?.sscc.id}</strong>. Please select how to handle this ASN.
            </Alert>

            <Box
              sx={{
                p: 2.5,
                borderRadius: 1.5,
                backgroundColor: 'background.paper',
                border: '1px solid rgba(0,0,0,0.08)'
              }}>
              <Typography
                variant="caption"
                sx={{
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  color: 'text.secondary',
                  mb: 2,
                  display: 'block'
                }}>
                ASN Details
              </Typography>
              <Stack spacing={2}>
                <Stack direction="row" spacing={3}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                      ASN Number
                    </Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ mt: 0.5 }}>
                      {selectedASN?.pkg.asn || 'N/A'}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                      PO Number
                    </Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ mt: 0.5 }}>
                      {selectedASN?.pkg.po || 'N/A'}
                    </Typography>
                  </Box>
                </Stack>
                <Stack direction="row" spacing={3}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                      Current SSCC
                    </Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ mt: 0.5 }}>
                      {selectedASN?.sscc.id}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                      Site
                    </Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ mt: 0.5 }}>
                      {selectedASN?.site.name}
                    </Typography>
                  </Box>
                </Stack>
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: '0.75rem', mb: 1, display: 'block' }}>
                    Current Status
                  </Typography>
                  <Chip
                    label={selectedASN?.pkg.status || 'Unknown'}
                    size="small"
                    color={selectedASN?.pkg.status === ReceiptPoStatus.available ? 'success' : 'default'}
                    sx={{ fontWeight: 600 }}
                  />
                </Box>
              </Stack>
            </Box>

            <Box>
              <Typography
                variant="caption"
                sx={{
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  color: 'text.secondary',
                  mb: 1.5,
                  display: 'block'
                }}>
                Select Action
              </Typography>
              <RadioGroup
                value={removalReason}
                onChange={(e) => setRemovalReason(e.target.value as RemovalReason)}
                sx={{ gap: 1 }}>
                <Box
                  sx={{
                    border: '2px solid',
                    borderColor: removalReason === RemovalReason.PriorityShipping ? 'primary.main' : 'rgba(0,0,0,0.12)',
                    borderRadius: 1.5,
                    p: 2,
                    transition: 'all 0.2s',
                    cursor: 'pointer',
                    backgroundColor:
                      removalReason === RemovalReason.PriorityShipping
                        ? 'rgba(25, 118, 210, 0.04)'
                        : 'background.default',
                    '&:hover': {
                      borderColor:
                        removalReason === RemovalReason.PriorityShipping ? 'primary.main' : 'rgba(0,0,0,0.3)',
                      backgroundColor:
                        removalReason === RemovalReason.PriorityShipping
                          ? 'rgba(25, 118, 210, 0.04)'
                          : 'rgba(0,0,0,0.02)'
                    }
                  }}
                  onClick={() => setRemovalReason(RemovalReason.PriorityShipping)}>
                  <FormControlLabel
                    value={RemovalReason.PriorityShipping}
                    control={<Radio />}
                    label={
                      <Box sx={{ ml: 1 }}>
                        <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                          Priority Shipping
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.5 }}>
                          Mark this ASN as 'available' for immediate processing
                        </Typography>
                      </Box>
                    }
                    sx={{ m: 0, width: '100%' }}
                  />
                </Box>
                <Box
                  sx={{
                    border: '2px solid',
                    borderColor: removalReason === RemovalReason.AppendToSSCC ? 'primary.main' : 'rgba(0,0,0,0.12)',
                    borderRadius: 1.5,
                    p: 2,
                    transition: 'all 0.2s',
                    cursor: 'pointer',
                    backgroundColor:
                      removalReason === RemovalReason.AppendToSSCC ? 'rgba(25, 118, 210, 0.04)' : 'background.default',
                    '&:hover': {
                      borderColor: removalReason === RemovalReason.AppendToSSCC ? 'primary.main' : 'rgba(0,0,0,0.3)',
                      backgroundColor:
                        removalReason === RemovalReason.AppendToSSCC ? 'rgba(25, 118, 210, 0.04)' : 'rgba(0,0,0,0.02)'
                    }
                  }}
                  onClick={() => setRemovalReason(RemovalReason.AppendToSSCC)}>
                  <FormControlLabel
                    value={RemovalReason.AppendToSSCC}
                    control={<Radio />}
                    label={
                      <Box sx={{ ml: 1 }}>
                        <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                          Append to Another SSCC
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.5 }}>
                          Move this ASN to a different SSCC (ASN will adopt the new SSCC's status)
                        </Typography>
                      </Box>
                    }
                    sx={{ m: 0, width: '100%' }}
                  />
                </Box>
              </RadioGroup>
            </Box>

            {removalReason === RemovalReason.AppendToSSCC && (
              <Box
                sx={{
                  p: 2.5,
                  borderRadius: 1.5,
                  backgroundColor: 'rgba(25, 118, 210, 0.04)',
                  border: '1px solid rgba(25, 118, 210, 0.2)'
                }}>
                <Typography
                  variant="caption"
                  sx={{
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    color: 'text.secondary',
                    mb: 1.5,
                    display: 'block'
                  }}>
                  Target SSCC
                </Typography>
                <Autocomplete
                  options={availableSSCCs}
                  getOptionLabel={(option) => `${option.id} - ${option.status || 'Unknown'}`}
                  value={availableSSCCs.find((s) => s.id === targetSSCC) || null}
                  onChange={(_, newValue) => setTargetSSCC(newValue?.id || '')}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Search for SSCC..."
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'background.default',
                          borderRadius: 1.5
                        }
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: '100%', py: 1 }}>
                        <Box
                          sx={{
                            p: 1,
                            borderRadius: 1,
                            backgroundColor: 'rgba(25, 118, 210, 0.08)',
                            display: 'flex'
                          }}>
                          <InventoryIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                        </Box>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="body2" fontWeight={600}>
                            {option.id}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {option.destinationLocation?.name || 'Unknown destination'} • {option.status || 'Unknown'}
                          </Typography>
                        </Box>
                      </Stack>
                    </li>
                  )}
                />
              </Box>
            )}

            <Box>
              <Typography
                variant="caption"
                sx={{
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  color: 'text.secondary',
                  mb: 1.5,
                  display: 'block'
                }}>
                Reason for Action *
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                required
                value={actionReason}
                onChange={(e) => setActionReason(e.target.value)}
                placeholder={
                  removalReason === RemovalReason.PriorityShipping
                    ? 'Explain why this ASN needs to be marked as available for priority shipping...'
                    : 'Explain why this ASN is being moved to another SSCC...'
                }
                helperText="This reason will be logged in the audit trail"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5,
                    backgroundColor: 'background.default'
                  },
                  '& .MuiFormHelperText-root': {
                    mx: 0,
                    mt: 1
                  }
                }}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2.5,
            backgroundColor: 'background.paper',
            borderTop: '1px solid rgba(0,0,0,0.08)'
          }}>
          <Button
            onClick={() => {
              setOpenRemoveDialog(false);
              setActionReason('');
            }}
            sx={{
              px: 3,
              py: 1,
              textTransform: 'none',
              fontWeight: 500,
              color: 'text.secondary'
            }}>
            Cancel
          </Button>
          <StyledButton
            variant="contained"
            color="error"
            onClick={handleRemoveASN}
            disabled={!actionReason.trim() || (removalReason === RemovalReason.AppendToSSCC && !targetSSCC)}
            startIcon={<DeleteOutlineIcon />}
            sx={{
              px: 3,
              py: 1,
              fontWeight: 500,
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': {
                boxShadow: 'none'
              }
            }}>
            {removalReason === RemovalReason.PriorityShipping ? 'Mark as Available' : 'Move ASN'}
          </StyledButton>
        </DialogActions>
      </Dialog>

      {/* Add ASN to SSCC Dialog */}
      <Dialog
        open={openAddASNDialog}
        onClose={() => {
          setOpenAddASNDialog(false);
          setAsnNumber('');
          setAddASNReason('');
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}>
        <DialogTitle
          sx={{
            borderBottom: '1px solid rgba(0,0,0,0.08)',
            px: 3,
            py: 2.5,
            backgroundColor: '#f9fafb'
          }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                p: 1,
                borderRadius: 1.5,
                backgroundColor: 'background.default',
                display: 'flex',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
              }}>
              <AddIcon sx={{ fontSize: 22, color: '#1976d2' }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={600}>
                Add ASN to SSCC
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Add a delivered ASN that's not in any SSCC
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 3 }}>
          <Stack spacing={3}>
            <Alert
              severity="info"
              icon={<InfoOutlinedIcon />}
              sx={{
                borderRadius: 1.5,
                border: '1px solid rgba(2, 136, 209, 0.2)'
              }}>
              Adding this ASN will adopt the SSCC's current status.
            </Alert>

            <Box
              sx={{
                p: 2.5,
                borderRadius: 1.5,
                backgroundColor: 'background.paper',
                border: '1px solid rgba(0,0,0,0.08)'
              }}>
              <Stack spacing={2}>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      color: 'text.secondary'
                    }}>
                    SSCC ID
                  </Typography>
                  <Typography variant="body1" fontWeight={600} sx={{ mt: 0.5 }}>
                    {selectedSSCCForASN?.id}
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      color: 'text.secondary'
                    }}>
                    Current Status
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Chip label={selectedSSCCForASN?.status || 'Unknown'} size="small" color="primary" />
                  </Box>
                </Box>
              </Stack>
            </Box>

            <Box>
              <Typography
                variant="caption"
                sx={{
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  color: 'text.secondary',
                  mb: 1.5,
                  display: 'block'
                }}>
                Select ASN *
              </Typography>
              <Autocomplete
                options={availableASNs}
                getOptionLabel={(option) => `${option.asn} - ${option.supplierName || 'Unknown'}`}
                value={availableASNs.find((a) => a.asn === asnNumber) || null}
                onChange={(_, newValue) => setAsnNumber(newValue?.asn || '')}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search for ASN..."
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1.5
                      }
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props}>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: '100%', py: 1 }}>
                      <Box
                        sx={{
                          p: 1,
                          borderRadius: 1,
                          backgroundColor: 'rgba(25, 118, 210, 0.08)',
                          display: 'flex'
                        }}>
                        <DescriptionIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                      </Box>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="body2" fontWeight={600}>
                          {option.asn}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.supplierName || 'Unknown supplier'} • PO: {option.po || 'N/A'} • Source:{' '}
                          {option.sourceSSCC}
                        </Typography>
                      </Box>
                    </Stack>
                  </li>
                )}
              />
            </Box>

            <Box>
              <Typography
                variant="caption"
                sx={{
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  color: 'text.secondary',
                  mb: 1.5,
                  display: 'block'
                }}>
                Reason for Adding *
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                required
                value={addASNReason}
                onChange={(e) => setAddASNReason(e.target.value)}
                placeholder="Explain why this ASN is being added to the SSCC (e.g., delivered without SSCC, manual assignment, etc.)..."
                helperText="This reason will be logged in the audit trail"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5
                  }
                }}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2.5,
            backgroundColor: 'background.paper',
            borderTop: '1px solid rgba(0,0,0,0.08)'
          }}>
          <Button
            onClick={() => {
              setOpenAddASNDialog(false);
              setAsnNumber('');
              setAddASNReason('');
            }}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              color: 'text.secondary'
            }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleAddASNToSSCC}
            disabled={!asnNumber || !addASNReason.trim() || isLoading}
            startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <AddIcon sx={{ fontSize: 16 }} />}
            sx={{
              px: 2,
              py: 0.75,
              fontWeight: 500,
              fontSize: '0.8rem',
              textTransform: 'none',
              backgroundColor: '#1976d2',
              '&:hover': { backgroundColor: '#1565c0' },
              boxShadow: 'none',
              borderRadius: 1
            }}>
            {isLoading ? 'Adding...' : 'Add ASN'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Complete SSCC Dialog */}
      <Dialog
        open={openCompleteDialog}
        onClose={() => {
          setOpenCompleteDialog(false);
          setCompleteReason('');
          setSelectedSSCCForComplete(null);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}>
        <DialogTitle
          sx={{
            borderBottom: '1px solid rgba(0,0,0,0.08)',
            px: 3,
            py: 2.5,
            backgroundColor: '#f0fdf4'
          }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                p: 1,
                borderRadius: 1.5,
                backgroundColor: 'background.default',
                display: 'flex',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
              }}>
              <CheckCircleIcon sx={{ fontSize: 22, color: '#2e7d32' }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={600}>
                Complete SSCC
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Mark this SSCC as delivered/completed
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 3 }}>
          <Stack spacing={3}>
            <Alert
              severity="info"
              icon={<CheckCircleIcon />}
              sx={{
                borderRadius: 1.5,
                border: '1px solid rgba(2, 136, 209, 0.2)'
              }}>
              This action will mark the SSCC as <strong>Delivered</strong>. Use this for stagnant SSCCs that have been
              closed in an upstream system but not in FTL.
            </Alert>

            <Box
              sx={{
                p: 2.5,
                borderRadius: 1.5,
                backgroundColor: 'background.paper',
                border: '1px solid rgba(0,0,0,0.08)'
              }}>
              <Typography
                variant="caption"
                sx={{
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  color: 'text.secondary',
                  mb: 2,
                  display: 'block'
                }}>
                SSCC Details
              </Typography>
              <Stack spacing={2}>
                <Stack direction="row" spacing={3}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                      SSCC ID
                    </Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ mt: 0.5 }}>
                      {selectedSSCCForComplete?.id}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                      Current Status
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Chip label={selectedSSCCForComplete?.status || 'Unknown'} size="small" color="primary" />
                    </Box>
                  </Box>
                </Stack>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    Total ASNs
                  </Typography>
                  <Typography variant="body2" fontWeight={600} sx={{ mt: 0.5 }}>
                    {selectedSSCCForComplete?.receiptedEvents.reduce((acc, site) => acc + site.packages.length, 0)} ASNs
                  </Typography>
                </Box>
              </Stack>
            </Box>

            <Box>
              <Typography
                variant="caption"
                sx={{
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  color: 'text.secondary',
                  mb: 1.5,
                  display: 'block'
                }}>
                Reason for Completing *
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                required
                value={completeReason}
                onChange={(e) => setCompleteReason(e.target.value)}
                placeholder="Explain why this SSCC is being marked as complete (e.g., closed in upstream system, stagnant SSCC cleanup, etc.)..."
                helperText="This reason will be logged in the audit trail"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5,
                    backgroundColor: 'background.default'
                  },
                  '& .MuiFormHelperText-root': {
                    mx: 0,
                    mt: 1
                  }
                }}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2.5,
            backgroundColor: 'background.paper',
            borderTop: '1px solid rgba(0,0,0,0.08)'
          }}>
          <Button
            onClick={() => {
              setOpenCompleteDialog(false);
              setCompleteReason('');
              setSelectedSSCCForComplete(null);
            }}
            sx={{
              px: 3,
              py: 1,
              textTransform: 'none',
              fontWeight: 500,
              color: 'text.secondary'
            }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCompleteSSCC}
            disabled={!completeReason.trim() || isLoading}
            startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
            sx={{
              px: 3,
              py: 1,
              fontWeight: 500,
              textTransform: 'none',
              backgroundColor: '#2e7d32',
              '&:hover': { backgroundColor: '#1b5e20' },
              boxShadow: 'none'
            }}>
            {isLoading ? 'Completing...' : 'Complete SSCC'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Remove ASNs Dialog */}
      <Dialog
        open={openBulkRemoveDialog}
        onClose={() => {
          if (!isLoading) {
            setOpenBulkRemoveDialog(false);
            setBulkRemoveReason('');
          }
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}>
        <DialogTitle
          sx={{
            borderBottom: '1px solid rgba(0,0,0,0.08)',
            px: 3,
            py: 2.5,
            backgroundColor: '#fef6f6'
          }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                p: 1,
                borderRadius: 1.5,
                backgroundColor: 'background.default',
                display: 'flex',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
              }}>
              <DeleteOutlineIcon sx={{ fontSize: 22, color: '#d32f2f' }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={600}>
                Remove Duplicate ASNs
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Permanently remove {selectedASNs.size} selected ASN(s) from this SSCC
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 3 }}>
          <Stack spacing={3}>
            <Alert
              severity="error"
              icon={<DeleteOutlineIcon />}
              sx={{
                borderRadius: 1.5,
                border: '1px solid rgba(211, 47, 47, 0.3)'
              }}>
              <Typography variant="body2">
                <strong>Warning:</strong> This action will permanently delete the selected ASNs. This cannot be undone.
              </Typography>
            </Alert>

            <Box
              sx={{
                p: 2.5,
                borderRadius: 1.5,
                backgroundColor: 'background.paper',
                border: '1px solid rgba(0,0,0,0.08)',
                maxHeight: 300,
                overflow: 'auto'
              }}>
              <Typography
                variant="caption"
                sx={{
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  color: 'text.secondary',
                  mb: 2,
                  display: 'block'
                }}>
                Selected ASNs ({selectedASNs.size})
              </Typography>
              <Stack spacing={1}>
                {Array.from(selectedASNs.values()).map(({ pkg, site }, index) => (
                  <Box
                    key={index}
                    sx={{
                      p: 1.5,
                      backgroundColor: 'background.default',
                      borderRadius: 1,
                      border: '1px solid rgba(211, 47, 47, 0.2)'
                    }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <DescriptionIcon sx={{ fontSize: 18, color: '#d32f2f' }} />
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="body2" fontWeight={600}>
                          ASN: {pkg.asn || 'N/A'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          PO: {pkg.po || 'N/A'} | Site: {site?.name || pkg.siteName || 'Unknown'}
                        </Typography>
                      </Box>
                      <Chip label={pkg.status || 'Unknown'} size="small" color="default" />
                    </Stack>
                  </Box>
                ))}
              </Stack>
            </Box>

            <Box>
              <Typography
                variant="caption"
                sx={{
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  color: 'text.secondary',
                  mb: 1.5,
                  display: 'block'
                }}>
                Reason for Removal *
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                required
                value={bulkRemoveReason}
                onChange={(e) => setBulkRemoveReason(e.target.value)}
                placeholder="Explain why these ASNs are being removed (e.g., duplicate entries, data error correction, etc.)..."
                helperText="This reason will be logged in the audit trail"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5,
                    backgroundColor: 'background.default'
                  },
                  '& .MuiFormHelperText-root': {
                    mx: 0,
                    mt: 1
                  }
                }}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2.5,
            backgroundColor: 'background.paper',
            borderTop: '1px solid rgba(0,0,0,0.08)'
          }}>
          <Button
            onClick={() => {
              setOpenBulkRemoveDialog(false);
              setBulkRemoveReason('');
            }}
            disabled={isLoading}
            sx={{
              px: 3,
              py: 1,
              textTransform: 'none',
              fontWeight: 500,
              color: 'text.secondary'
            }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleBulkRemoveASNs}
            disabled={!bulkRemoveReason.trim() || isLoading}
            startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineIcon />}
            sx={{
              px: 3,
              py: 1,
              fontWeight: 500,
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': { boxShadow: 'none' }
            }}>
            {isLoading ? 'Removing...' : `Remove ${selectedASNs.size} ASN(s)`}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
